import { c as _c } from "react/compiler-runtime";
import { IconButton } from '@repo/design-system/components/ds/button';
import { Icon } from '@repo/design-system/components/ds/icon';
import { FlexColumn } from '@repo/design-system/components/layout/flex-column';
import { Type } from '@repo/design-system/components/ui/type';
import type { MaterialSymbolName } from '@repo/design-system/types/material-symbol-names';
import { useT } from '@repo/i18n/components/i18n.client';
import { css, cx } from '@repo/panda/css';
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getHeroActionIcon } from '../location-info/hero-action-icons';
import type { ExploreAction } from './explore-actions';
type ExploreActionMenuVariant = 'desktopSheet' | 'mobileSheet';
interface ExploreActionMenuProps {
  actions: ExploreAction[];
  variant: ExploreActionMenuVariant;
  selectedActionId?: string | null;
  isAskLoading?: boolean;
  onSelect: (action: ExploreAction) => void;
  onAskSubmit: (query: string) => void;
}
const sheetMenuStyles = css.raw({
  width: 'full',
  paddingY: '8',
  backgroundColor: 'transparent'
});
const menuItemStyles = css({
  width: 'full',
  display: 'flex',
  alignItems: 'center',
  gap: '10',
  padding: '12',
  border: 'none',
  borderRadius: '12',
  backgroundColor: 'transparent',
  color: 'white',
  cursor: 'pointer',
  textAlign: 'left',
  outline: 'none',
  _hover: {
    backgroundColor: 'whiteAlpha.10'
  },
  _focusVisible: {
    backgroundColor: 'whiteAlpha.10',
    outline: '[2px solid]',
    outlineColor: 'blue.500'
  }
});
const mobileMenuItemStyles = css({
  height: '[44px]',
  minHeight: '[44px]',
  padding: '12',
  borderRadius: '12',
  _hover: {
    backgroundColor: 'whiteAlpha.10'
  }
});
const sheetAskFormStyles = css({
  width: 'full',
  height: '[44px]',
  display: 'flex',
  alignItems: 'center',
  gap: '8',
  border: 'none',
  borderRadius: '12',
  padding: '[4px 4px 4px 12px]',
  backgroundColor: 'whiteAlpha.10',
  color: 'white'
});
const sheetAskInputStyles = css({
  minWidth: '0',
  flex: '1',
  height: 'full',
  border: 'none',
  outline: 'none',
  backgroundColor: 'transparent',
  color: '[inherit]',
  fontSize: 'md',
  fontWeight: 'medium',
  lineHeight: '[20px]',
  _placeholder: {
    color: '[inherit]',
    opacity: 0.5
  }
});
const sheetAskClearButtonStyles = css({
  width: '[32px]',
  height: '[32px]',
  minWidth: '[32px]',
  border: 'none',
  borderRadius: 'full',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'transparent',
  color: '[inherit]',
  cursor: 'pointer'
});
const submittedSheetAskFormStyle: React.CSSProperties = {
  backgroundColor: 'var(--colors-white)',
  color: 'var(--colors-black)'
};
const selectedNearbyMenuItemStyle: React.CSSProperties = {
  backgroundColor: 'var(--colors-white)',
  color: 'var(--colors-black)'
};
const selectedHeroActionMenuItemStyle: React.CSSProperties = {
  backgroundColor: 'var(--colors-brand-primary)',
  color: 'var(--colors-black)'
};
export const ExploreActionMenu: React.FC<ExploreActionMenuProps> = t0 => {
  const $ = _c(41);
  const {
    actions,
    variant,
    selectedActionId,
    isAskLoading: t1,
    onSelect,
    onAskSubmit
  } = t0;
  const isAskLoading = t1 === undefined ? false : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = ["game_map"];
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    t
  } = useT(t2);
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const askInputRef = useRef(null);
  let t3;
  if ($[1] !== actions) {
    t3 = actions.find(_temp);
    $[1] = actions;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const nearbyAction = t3;
  let t4;
  if ($[3] !== actions) {
    t4 = actions.filter(_temp2);
    $[3] = actions;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const checklistActions = t4;
  let t5;
  if ($[5] !== actions) {
    t5 = actions.filter(_temp3);
    $[5] = actions;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const heroActions = t5;
  let t6;
  if ($[7] !== actions) {
    t6 = actions.find(_temp4);
    $[7] = actions;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const askAction = t6;
  const askIsSelected = selectedActionId === askAction?.id;
  let t7;
  if ($[9] !== nearbyAction) {
    t7 = nearbyAction ? [nearbyAction] : [];
    $[9] = nearbyAction;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== askAction) {
    t8 = askAction ? [askAction] : [];
    $[11] = askAction;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== checklistActions || $[14] !== heroActions || $[15] !== t7 || $[16] !== t8) {
    t9 = [t7, checklistActions, heroActions, t8].filter(_temp5);
    $[13] = checklistActions;
    $[14] = heroActions;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const sections = t9;
  let t10;
  let t11;
  if ($[18] !== askIsSelected) {
    t10 = () => {
      if (!askIsSelected) {
        return;
      }
      askInputRef.current?.focus();
    };
    t11 = [askIsSelected];
    $[18] = askIsSelected;
    $[19] = t10;
    $[20] = t11;
  } else {
    t10 = $[19];
    t11 = $[20];
  }
  useEffect(t10, t11);
  let t12;
  if ($[21] !== askAction || $[22] !== isAskLoading || $[23] !== onAskSubmit || $[24] !== query) {
    t12 = e => {
      e.preventDefault();
      const trimmed = query.trim();
      if (!trimmed || !askAction || isAskLoading) {
        return;
      }
      setSubmittedQuery(trimmed);
      onAskSubmit(trimmed);
    };
    $[21] = askAction;
    $[22] = isAskLoading;
    $[23] = onAskSubmit;
    $[24] = query;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  const handleAskSubmit = t12;
  let t13;
  if ($[26] !== t) {
    t13 = t(_temp6);
    $[26] = t;
    $[27] = t13;
  } else {
    t13 = $[27];
  }
  let t14;
  if ($[28] !== askIsSelected || $[29] !== handleAskSubmit || $[30] !== isAskLoading || $[31] !== onSelect || $[32] !== query || $[33] !== sections || $[34] !== selectedActionId || $[35] !== submittedQuery || $[36] !== variant) {
    t14 = sections.map(section_0 => <FlexColumn key={section_0.map(_temp7).join(":")} gap="2" width="full">{section_0.map(action_4 => <ExploreActionMenuItem key={action_4.id} action={action_4} selected={selectedActionId === action_4.id} variant={variant} onSelect={onSelect} askIsSelected={askIsSelected && action_4.type === "ask_anything"} askQuery={query} askSubmitted={!!submittedQuery} askInputRef={askInputRef} isAskLoading={isAskLoading} onAskQueryChange={setQuery} onAskClear={() => {
        setQuery("");
        setSubmittedQuery("");
        askInputRef.current?.focus();
      }} onAskSubmit={handleAskSubmit} />)}</FlexColumn>);
    $[28] = askIsSelected;
    $[29] = handleAskSubmit;
    $[30] = isAskLoading;
    $[31] = onSelect;
    $[32] = query;
    $[33] = sections;
    $[34] = selectedActionId;
    $[35] = submittedQuery;
    $[36] = variant;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== t13 || $[39] !== t14) {
    t15 = <FlexColumn gap="4" className={sheetMenuStyles} role="group" aria-label={t13}>{t14}</FlexColumn>;
    $[38] = t13;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  return t15;
};
const ExploreActionMenuItem: React.FC<{
  action: ExploreAction;
  selected: boolean;
  variant: ExploreActionMenuVariant;
  askIsSelected: boolean;
  askQuery: string;
  askSubmitted: boolean;
  askInputRef: React.RefObject<HTMLInputElement | null>;
  isAskLoading: boolean;
  onSelect: (action: ExploreAction) => void;
  onAskQueryChange: (value: string) => void;
  onAskClear: () => void;
  onAskSubmit: (e: React.SubmitEvent<HTMLFormElement>) => void;
}> = t0 => {
  const $ = _c(66);
  const {
    action,
    selected,
    variant,
    askIsSelected,
    askQuery,
    askSubmitted,
    askInputRef,
    isAskLoading,
    onSelect,
    onAskQueryChange,
    onAskClear,
    onAskSubmit
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["game_map"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useT(t1);
  let t2;
  if ($[1] !== action) {
    t2 = getActionIcon(action);
    $[1] = action;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const icon = t2;
  let t3;
  if ($[3] !== action.heroAction || $[4] !== action.subcategory || $[5] !== action.type || $[6] !== t) {
    t3 = action.type === "nearby" ? t(_temp8) : action.type === "checklist" ? t(_temp9, {
      checklistName: action.subcategory.title
    }) : action.type === "hero_action" ? action.heroAction.name : t(_temp10);
    $[3] = action.heroAction;
    $[4] = action.subcategory;
    $[5] = action.type;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const label = t3;
  const showSelected = variant === "desktopSheet" && selected && !askIsSelected;
  const textColor = showSelected && (action.type === "nearby" || action.type === "hero_action") ? "black" : "white";
  let t4;
  if ($[8] !== action || $[9] !== showSelected) {
    t4 = getSelectedMenuItemStyle(action, showSelected);
    $[8] = action;
    $[9] = showSelected;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const selectedMenuItemStyle = t4;
  const fillIcon = action.type === "hero_action";
  if (askIsSelected) {
    const t5 = askSubmitted ? submittedSheetAskFormStyle : undefined;
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = css({
        flexShrink: "0"
      });
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    let t7;
    if ($[12] !== fillIcon || $[13] !== icon || $[14] !== t6) {
      t7 = <Icon icon={icon} fill={fillIcon} size="sm" className={t6} decorative={true} />;
      $[12] = fillIcon;
      $[13] = icon;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== onAskQueryChange) {
      t8 = e => onAskQueryChange(e.target.value);
      $[16] = onAskQueryChange;
      $[17] = t8;
    } else {
      t8 = $[17];
    }
    let t9;
    if ($[18] !== t) {
      t9 = t(_temp11);
      $[18] = t;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] !== t) {
      t10 = t(_temp12);
      $[20] = t;
      $[21] = t10;
    } else {
      t10 = $[21];
    }
    let t11;
    if ($[22] !== askInputRef || $[23] !== askQuery || $[24] !== askSubmitted || $[25] !== isAskLoading || $[26] !== t10 || $[27] !== t8 || $[28] !== t9) {
      t11 = <input ref={askInputRef} className={sheetAskInputStyles} value={askQuery} onChange={t8} placeholder={t9} aria-label={t10} disabled={isAskLoading} readOnly={askSubmitted} />;
      $[22] = askInputRef;
      $[23] = askQuery;
      $[24] = askSubmitted;
      $[25] = isAskLoading;
      $[26] = t10;
      $[27] = t8;
      $[28] = t9;
      $[29] = t11;
    } else {
      t11 = $[29];
    }
    let t12;
    if ($[30] !== askQuery || $[31] !== askSubmitted || $[32] !== isAskLoading || $[33] !== onAskClear || $[34] !== t) {
      t12 = askSubmitted ? <button type="button" className={sheetAskClearButtonStyles} onClick={onAskClear}><Icon icon="close" decorative={true} className={css({
          fontSize: "[12px]"
        })} /></button> : <IconButton type="submit" color="brand" size="sm" disabled={!askQuery.trim() || isAskLoading} aria-label={t(_temp13)}><Icon icon="arrow_forward" decorative={true} /></IconButton>;
      $[30] = askQuery;
      $[31] = askSubmitted;
      $[32] = isAskLoading;
      $[33] = onAskClear;
      $[34] = t;
      $[35] = t12;
    } else {
      t12 = $[35];
    }
    let t13;
    if ($[36] !== onAskSubmit || $[37] !== t11 || $[38] !== t12 || $[39] !== t5 || $[40] !== t7) {
      t13 = <form onSubmit={onAskSubmit} className={sheetAskFormStyles} style={t5}>{t7}{t11}{t12}</form>;
      $[36] = onAskSubmit;
      $[37] = t11;
      $[38] = t12;
      $[39] = t5;
      $[40] = t7;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    return t13;
  }
  let t5;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cx(menuItemStyles, mobileMenuItemStyles);
    $[42] = t5;
  } else {
    t5 = $[42];
  }
  let t6;
  if ($[43] !== action || $[44] !== onSelect) {
    t6 = () => onSelect(action);
    $[43] = action;
    $[44] = onSelect;
    $[45] = t6;
  } else {
    t6 = $[45];
  }
  let t7;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = css({
      flexShrink: "0"
    });
    $[46] = t7;
  } else {
    t7 = $[46];
  }
  let t8;
  if ($[47] !== fillIcon || $[48] !== icon || $[49] !== t7) {
    t8 = <Icon icon={icon} fill={fillIcon} size="sm" className={t7} decorative={true} />;
    $[47] = fillIcon;
    $[48] = icon;
    $[49] = t7;
    $[50] = t8;
  } else {
    t8 = $[50];
  }
  let t9;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = css.raw({
      minWidth: "0",
      flex: "1"
    });
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  let t10;
  if ($[52] !== label || $[53] !== textColor) {
    t10 = <Type variant="header-xs" color={textColor} truncate={true} title={label}>{label}</Type>;
    $[52] = label;
    $[53] = textColor;
    $[54] = t10;
  } else {
    t10 = $[54];
  }
  let t11;
  if ($[55] !== t10 || $[56] !== t9) {
    t11 = <FlexColumn className={t9}>{t10}</FlexColumn>;
    $[55] = t10;
    $[56] = t9;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  let t12;
  if ($[58] !== showSelected) {
    t12 = showSelected && <Icon icon="check" size="sm" decorative={true} />;
    $[58] = showSelected;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== selectedMenuItemStyle || $[61] !== t11 || $[62] !== t12 || $[63] !== t6 || $[64] !== t8) {
    t13 = <button type="button" className={t5} onClick={t6} style={selectedMenuItemStyle}>{t8}{t11}{t12}</button>;
    $[60] = selectedMenuItemStyle;
    $[61] = t11;
    $[62] = t12;
    $[63] = t6;
    $[64] = t8;
    $[65] = t13;
  } else {
    t13 = $[65];
  }
  return t13;
};
function getActionIcon(action: ExploreAction): MaterialSymbolName {
  if (action.type === 'nearby') return 'sensors';
  if (action.type === 'checklist') return 'checklist';
  if (action.type === 'hero_action') return getHeroActionIcon(action.heroAction.iconCategory);
  return 'wand_stars';
}
function getSelectedMenuItemStyle(action: ExploreAction, showSelected: boolean): React.CSSProperties | undefined {
  if (!showSelected) return undefined;
  if (action.type === 'nearby') return selectedNearbyMenuItemStyle;
  if (action.type === 'checklist') return {
    backgroundColor: action.category.color
  };
  if (action.type === 'hero_action') return selectedHeroActionMenuItemStyle;
  return undefined;
}
function _temp(action) {
  return action.type === "nearby";
}
function _temp2(action_0) {
  return action_0.type === "checklist";
}
function _temp3(action_1) {
  return action_1.type === "hero_action";
}
function _temp4(action_2) {
  return action_2.type === "ask_anything";
}
function _temp5(section) {
  return section.length > 0;
}
function _temp6($) {
  return $.explore_menu.menu_aria_label;
}
function _temp7(action_3) {
  return action_3.id;
}
function _temp8($) {
  return $.nearby_pins;
}
function _temp9($_0) {
  return $_0.explore_menu.checklist_label;
}
function _temp10($_1) {
  return $_1.explore_menu.ask_placeholder;
}
function _temp11($_2) {
  return $_2.explore_menu.ask_placeholder;
}
function _temp12($_3) {
  return $_3.explore_menu.ask_aria_label;
}
function _temp13($_4) {
  return $_4.explore_menu.ask_submit;
}